/* Eden enhancements. Every hero owns and cleans up its listeners, observers and frames. */
(() => {
  if (customElements.get('eden-hero')) return;
  const option = (name, fallback) => {
    const value = parseFloat(getComputedStyle(document.body).getPropertyValue(`--eden-${name}`));
    return Number.isFinite(value) ? value : fallback;
  };
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  class EdenHero extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.art = this.querySelector('.eden-hero-art');
      this.image = this.art?.querySelector('img');
      this.panel = this.querySelector('.eden-instruments');
      this.states = Array.from(this.querySelectorAll('.eden-fader')).map((label, index) => {
        const input = label.querySelector('input');
        return { input, control: label.querySelector('.eden-fader-control'), knob: label.querySelector('.eden-fader-knob'), index, target: Number(input.value), shown: Number(input.value), touched: false };
      });
      this.options = {
        duration: option('intro-duration', 3800), travel: option('intro-travel', 28),
        stagger: option('intro-stagger', 160), cycles: option('intro-cycles', 2),
        panelX: option('panel-x', 50.2)/100, panelY: option('panel-y', 42.9)/100,
        panelWidth: option('panel-width', 55)/100, panelMobileWidth: option('panel-mobile-width', 46)/100, panelHeight: option('panel-height',27)/100,
        damping: option('fader-damping', 38), parallax: option('parallax', 1),
        strength: option('parallax-strength', 10) / 100, max: option('parallax-max', 65)
      };
      this.introStart = performance.now();
      this.introWaiting = document.documentElement.classList.contains("eden-intro-active");
      document.addEventListener("eden:intro:done", () => { this.introWaiting = false; this.introStart = performance.now(); this.wake(); }, { signal });
      this.frame = 0; this.scrollFrame = 0; this.lastTime = 0;
      this.states.forEach(state => {
        const inputChanged = () => { state.touched = true; state.target = Number(state.input.value); this.wake(); };
        state.input.addEventListener('pointerdown', () => { state.control.classList.remove('eden-keyboard-focus'); inputChanged(); }, { signal });
        state.input.addEventListener('keydown', () => { state.control.classList.add('eden-keyboard-focus'); inputChanged(); }, { signal });
        state.input.addEventListener('blur', () => state.control.classList.remove('eden-keyboard-focus'), { signal });
        state.input.addEventListener('input', inputChanged, { signal });
      });
      this.positionPanel();
      this.setAttribute('data-eden-ready', '');
      this.resize = new ResizeObserver(() => this.positionPanel());
      if (this.art) this.resize.observe(this.art);
      this.image?.addEventListener('load', () => this.positionPanel(), { signal });
      window.addEventListener('resize', () => this.positionPanel(), { signal, passive: true });
      /**
       * Horizon scrolls `.page-wrapper` at >=990px and `document.scrollingElement` below it.
       * Scroll events don't bubble, so a window listener alone never fires on desktop; the
       * capture-phase listener on `document` sees both containers.
       */
      const onScroll = () => {
        if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = 0; this.parallax(); });
      };
      document.addEventListener('scroll', onScroll, { signal, passive: true, capture: true });
      window.addEventListener('scroll', onScroll, { signal, passive: true });
      reduced.addEventListener('change', () => { this.parallax(); this.wake(); }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { cancelAnimationFrame(this.frame); this.frame = 0; }
        else this.wake();
      }, { signal });
      this.wake();
    }
    get motion() { return this.dataset.motion === 'true' && !reduced.matches; }
    positionPanel() {
      if (!this.art || !this.panel) return;
      const w = this.art.clientWidth, h = this.art.clientHeight;
      const mobile = window.innerWidth < 750;
      const scale = Math.max(w / 3548, h / 1774);
      const iw = mobile ? w : 3548 * scale, ih = mobile ? h : 1774 * scale;
      const x = (w - iw) * (window.innerWidth <= 1000 && !mobile ? .62 : .5), y = (h - ih) * .5;
      Object.assign(this.panel.style, { left: `${x + iw * this.options.panelX}px`, top: `${y + ih * this.options.panelY}px`, width: `${iw * (mobile ? this.options.panelMobileWidth : this.options.panelWidth)}px`, height: `${ih * this.options.panelHeight}px` });
      this.parallax();
    }
    parallax() {
      if (!this.art) return;
      const top = this.getBoundingClientRect().top;
      this.art.style.transform = this.motion && !this.hasAttribute("data-eden-exit") && this.options.parallax && innerWidth >= 750 && top < 0 ? `translateY(${Math.min(-top * this.options.strength, this.options.max)}px)` : '';
    }
    wake() {
      if (!this.frame && !document.hidden && this.isConnected) {
        this.lastTime = 0;
        this.frame = requestAnimationFrame(time => this.draw(time));
      }
    }
    draw(time) {
      const dt = Math.min(50, time - (this.lastTime || time - 16)); this.lastTime = time;
      let active = false;
      this.states.forEach(state => {
        const elapsed = time - this.introStart - state.index * this.options.stagger;
        const intro = !this.introWaiting && this.motion && this.dataset.intro === 'true' && !window.Shopify?.designMode && !state.touched && elapsed < this.options.duration;
        let target = state.target;
        if (intro && elapsed > 0) {
          const t = Math.min(1, elapsed / this.options.duration);
          // Squared envelope: zero value *and* zero slope at both ends, so the
          // swing starts and settles without a visible kick.
          const envelope = Math.sin(t * Math.PI) ** 2;
          const wave = Math.sin(t * Math.PI * 2 * this.options.cycles) * envelope;
          // One symmetric amplitude for the whole swing. Picking it from the sign
          // of the wave used to flip it at every zero crossing — the value stayed
          // continuous but the slope did not, which is what read as a stutter.
          const headroom = Math.min(state.target, 100 - state.target);
          const travel = Math.min(this.options.travel, headroom);
          target += wave * travel;
        }
        target = Math.max(0, Math.min(100, target));
        state.shown = this.motion && this.options.damping > 0 ? state.shown + (target - state.shown) * (1 - Math.exp(-dt / this.options.damping)) : target;
        if (Math.abs(target - state.shown) < .025) state.shown = target;
        state.knob.style.setProperty('--eden-level', state.shown / 100);
        if (intro || Math.abs(target - state.shown) > .025) active = true;
      });
      this.frame = active ? requestAnimationFrame(next => this.draw(next)) : 0;
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.resize?.disconnect(); cancelAnimationFrame(this.frame); cancelAnimationFrame(this.scrollFrame);
      this.frame = 0; this.scrollFrame = 0;
    }
  }
  customElements.define('eden-hero', EdenHero);
})();
